import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react'

import { Icons } from 'atoms'
import * as DocumentPicker from 'expo-document-picker'
import {
  DIRECTORY_TYPE_ENUM,
  EXTENSION_TYPE_ENUM,
  useUploadMutation
} from 'integration/resources/upload'
import { Box, HStack, IconButton, Pressable, Spinner, Text, VStack } from 'native-base'
import { Platform } from 'react-native'

import { OpportunityDeletionAttachment } from './types'

const MAX_ATTACHMENTS = 5

const MAX_TOTAL_SIZE_BYTES = 20 * 1024 * 1024

const DISPLAY_ALLOWED_EXTENSIONS = '.jpeg, .png, .pdf e .eml'

const ALLOWED_EXTENSIONS = ['jpeg', 'jpg', 'png', 'pdf', 'eml']

const ERROR_MESSAGES = {
  size: 'Limite de 20 MB excedido.',
  format: 'Formato de arquivo inválido.',
  quantity: 'Limite de 5 arquivos.',
  generic: 'Não foi possível anexar o arquivo. Tente novamente.'
} as const

type OpportunityDeletionAttachmentsFieldProps = {
  attachments: OpportunityDeletionAttachment[]
  onChange(attachments: OpportunityDeletionAttachment[]): void
  error: string | null
  onError(message: string | null): void
  onUploadStatusChange?(isUploading: boolean): void
}

const normalizeExtension = (name?: string, mimeType?: string) => {
  const fromName = name?.includes('.') ? name.split('.').pop()?.toLowerCase() : undefined

  const fromMime = mimeType?.split('/').pop()?.toLowerCase()

  if (fromName) {
    if (fromName === 'rfc822') return 'eml'

    return fromName
  }

  if (fromMime === 'rfc822') return 'eml'

  return fromMime ?? ''
}

const formatBytes = (size: number) => {
  if (size === 0) return '0 KB'

  const units = ['KB', 'MB']

  let value = size / 1024

  let unitIndex = 0

  if (value >= 1024) {
    value /= 1024

    unitIndex = 1
  }

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

const getAttachmentIcon = (extension: string): ComponentType<any> => {
  switch (extension) {
    case 'pdf':
      return Icons.PDF

    case 'png':
      return Icons.PNG

    case 'jpeg':
      return Icons.JPG

    case 'jpg':
      return Icons.JPG

    case 'eml':
      return Icons.ARQ

    default:
      return Icons.ARQ
  }
}

export const OpportunityDeletionAttachmentsField = ({
  attachments,
  onChange,
  error,
  onError,
  onUploadStatusChange
}: OpportunityDeletionAttachmentsFieldProps) => {
  const [isProcessing, setIsProcessing] = useState(false)

  const { mutateAsync: uploadAsync, isLoading: isUploading } = useUploadMutation()

  useEffect(() => {
    onUploadStatusChange?.(isProcessing || isUploading)
  }, [isProcessing, isUploading, onUploadStatusChange])

  const isBusy = isProcessing || isUploading

  const totalSize = useMemo(
    () => attachments.reduce((acc, item) => acc + (item.size ?? 0), 0),
    [attachments]
  )

  const handleRemoveAttachment = useCallback(
    (storageFileName: string) => {
      onError(null)

      onChange(attachments.filter((item) => item.storage_file_name !== storageFileName))
    },
    [attachments, onChange, onError]
  )

  const handlePickAttachment = useCallback(async () => {
    if (isProcessing || isUploading) return

    onError(null)

    if (attachments.length >= MAX_ATTACHMENTS) {
      onError(ERROR_MESSAGES.quantity)

      return
    }

    const pickerResult = await DocumentPicker.getDocumentAsync({
      copyToCacheDirectory: Platform.OS !== 'web',
      multiple: false
    })

    if (pickerResult.type === 'cancel') return

    setIsProcessing(true)

    try {
      let nextAttachments = [...attachments]

      const nextTotalSize = totalSize

      let extension = normalizeExtension(pickerResult.name, pickerResult.mimeType)

      if (!extension) {
        onError(ERROR_MESSAGES.format)

        return
      }

      extension = extension.toLowerCase()

      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        onError(ERROR_MESSAGES.format)

        return
      }

      const response = await fetch(pickerResult.uri)

      const file = await response.blob()

      const fileSize = file.size

      if (nextTotalSize + fileSize > MAX_TOTAL_SIZE_BYTES) {
        onError(ERROR_MESSAGES.size)

        return
      }

      const [uploadResult] = await uploadAsync({
        directory_type: DIRECTORY_TYPE_ENUM.OPPORTUNITY_ATTACHMENTS,
        extension_type: extension.toUpperCase() as keyof typeof EXTENSION_TYPE_ENUM,
        files: [file]
      })

      const { name: storedFileName, url, status } = uploadResult

      if (status < 200 || status >= 300 || !url) {
        onError(ERROR_MESSAGES.generic)

        return
      }

      nextAttachments = [
        ...nextAttachments,
        {
          file_name: pickerResult.name ?? storedFileName,
          storage_file_name: storedFileName,
          url,
          created_at: new Date().toISOString(),
          size: fileSize,
          extension
        }
      ]

      onChange(nextAttachments)

      onError(null)
    } catch (err) {
      onError(ERROR_MESSAGES.generic)
    } finally {
      setIsProcessing(false)
    }
  }, [attachments, isProcessing, isUploading, onChange, onError, totalSize, uploadAsync])

  const cardShadowStyle =
    Platform.OS === 'web'
      ? ({
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08), 0 0 4px 0 rgba(0, 0, 0, 0.08)'
        } as const)
      : undefined

  return (
    <VStack space={6} bg="white" borderRadius="24px" px={6} py={6} style={cardShadowStyle as any}>
      <Text fontSize="16px" lineHeight="24px" fontWeight={600} color="gray.700">
        Arquivos
      </Text>

      <Text fontSize="14px" lineHeight="20px" color="gray.400">
        Extensões permitidas: {DISPLAY_ALLOWED_EXTENSIONS}
      </Text>
      <Pressable
        onPress={handlePickAttachment}
        borderRadius="20px"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor="gray.200"
        bg="gray.50"
        px="auto"
        py={8}
        minH="104px"
        justifyContent="center"
        alignItems="center">
        <VStack alignItems="center" space={3}>
          {isBusy ? (
            <Spinner color="gray.500" size="lg" />
          ) : (
            <Text fontSize="16px" lineHeight="24px" color="gray.400" textAlign="center">
              Arraste seu arquivo aqui
            </Text>
          )}
        </VStack>
      </Pressable>

      {attachments.length > 0 && (
        <VStack space={4} mt={2}>
          {chunkArray(attachments, 2).map((row, rowIndex) => (
            <HStack key={`attachment-row-${rowIndex}`} space={6}>
              {row.map((attachment) => {
                const IconComponent = getAttachmentIcon(attachment.extension)

                return (
                  <HStack
                    key={attachment.storage_file_name}
                    alignItems="center"
                    space={3}
                    flex={1}
                    maxW="50%">
                    <IconComponent />
                    <VStack maxW="180px" space={0}>
                      <Text
                        numberOfLines={1}
                        fontSize="14px"
                        lineHeight="18px"
                        fontWeight={700}
                        color="gray.700">
                        {attachment.file_name}
                      </Text>
                      <Text fontSize="12px" lineHeight="16px" color="gray.500">
                        .{attachment.extension} {formatBytes(attachment.size)}
                      </Text>
                    </VStack>
                    <IconButton
                      onPress={() => handleRemoveAttachment(attachment.storage_file_name)}
                      icon={<Icons.Close size={4} color="gray.700" />}
                      borderRadius="full"
                      variant="ghost"
                      isDisabled={isBusy}
                      _pressed={{ bg: 'transparent', opacity: 0.4 }}
                      p={1}
                    />
                  </HStack>
                )
              })}

              {row.length === 1 && <Box flex={1} maxW="50%" />}
            </HStack>
          ))}
        </VStack>
      )}

      <Text fontSize="12px" lineHeight="16px" color="gray.500" alignSelf="flex-end">
        Quantidade máxima: {attachments.length}/{MAX_ATTACHMENTS}
      </Text>

      {error && (
        <Text fontSize="12px" lineHeight="16px" color="error.500">
          {error}
        </Text>
      )}
    </VStack>
  )
}

const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
  if (!chunkSize || chunkSize <= 0) return [array]

  return array.reduce<T[][]>((acc, item, index) => {
    if (index % chunkSize === 0) {
      acc.push([item])
    } else {
      acc[acc.length - 1].push(item)
    }

    return acc
  }, [])
}
